import styled from '@emotion/styled'
import type { CSSObject } from '@emotion/react'
import { WebLink } from '@news-mono/web-common'
import { Link } from 'react-router-dom'
import {
    mapTitleMixin,
    perthNowNGNTextMixin,
} from '../../../mixins/TextMixin/TextMixin.theme'
import {
    Breakpoints,
    calcRem,
    visuallyhidden,
    breakpoint,
    visuallyhiddenOff,
    easing,
} from '../../../__styling'
import { zIndex } from '../../../__styling/settings/z-index'
import {
    colorsPN,
    colorsPNCommonwealthGames,
    colorsPNLetsGoFreo,
    metricsPN,
    ThemeOverride,
} from '@news-mono/design-tokens'
import { TopicPageType } from './PNSectionHeader'

export interface SectionHeaderWrapperProps {
    marginBottom: number
    invisibleHeaderBreakpoint?: keyof Breakpoints
    topicHeaderType?: TopicPageType
    isGenericTopicPage?: boolean
    themeOverride?: ThemeOverride
}

const sectionHeaderThemeOverrides: Partial<Record<ThemeOverride, CSSObject>> = {
    'commonwealth-games': {
        backgroundColor: colorsPNCommonwealthGames.background.strong,
        borderRadius: metricsPN.radius.M,
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: calcRem(metricsPN.spacing['2XS']),
        padding: calcRem(metricsPN.spacing['2XS'], metricsPN.spacing['S']),
        h3: {
            color: colorsPNCommonwealthGames.text.primary,
            textDecorationColor: colorsPNCommonwealthGames.text.primary,
        },

        a: {
            color: colorsPNCommonwealthGames.text.secondary,
            textDecorationColor: colorsPNCommonwealthGames.text.secondary,
        },

        'a:hover, a:focus-visible': {
            textDecorationColor: colorsPNCommonwealthGames.text.secondary,
        },

        'a:has(h3)': {
            color: colorsPNCommonwealthGames.text.primary,
            textDecorationColor: colorsPNCommonwealthGames.text.primary,
        },

        'a:has(h3):hover, a:has(h3):focus-visible': {
            textDecorationColor: colorsPNCommonwealthGames.text.primary,
        },
    },
    'lets-go-freo': {
        backgroundColor: colorsPNLetsGoFreo.background.strong,
        borderRadius: calcRem(metricsPN.radius.M),
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: calcRem(metricsPN.spacing['2XS']),
        padding: calcRem(metricsPN.spacing['2XS'], metricsPN.spacing['S']),
        h3: {
            color: colorsPN.text.inverse,
            textDecorationColor: colorsPN.text.inverse,
        },

        a: {
            color: colorsPN.text.inverse,
            textDecorationColor: colorsPN.text.inverse,
        },

        'a:hover, a:focus-visible': {
            textDecorationColor: colorsPN.text.inverse,
        },

        'a:has(h3)': {
            color: colorsPN.text.inverse,
            textDecorationColor: colorsPN.text.inverse,
        },

        'a:has(h3):hover, a:has(h3):focus-visible': {
            textDecorationColor: colorsPN.text.inverse,
        },
    },
}

const sectionHeaderContainerThemeOverrides: Record<ThemeOverride, CSSObject> = {
    'commonwealth-games': {
        marginBottom: calcRem(metricsPN.spacing['2XS']),
    },
    'lets-go-freo': {
        marginBottom: calcRem(metricsPN.spacing['2XS']),
    },
    'video-carousel': {
        margin: calcRem(0, metricsPN.spacing['S']),
        [breakpoint('lg')]: {
            margin: 0,
        },
    },
}

export const SectionHeaderContainer = styled.div<SectionHeaderWrapperProps>(
    ({ invisibleHeaderBreakpoint, marginBottom, themeOverride }) => {
        return [
            {
                display: 'flex',
                flex: '1 0 auto',
                flexWrap: 'wrap',
                alignItems: 'flex-start',
                maxWidth: '100%',
                marginBottom: marginBottom,
                transition: 'margin 0.25s, padding 0.25s',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                flexDirection: 'column',
                gap: calcRem(metricsPN.spacing['4XS']),
            },
            invisibleHeaderBreakpoint !== undefined && {
                ...visuallyhidden,

                [breakpoint(invisibleHeaderBreakpoint || 'md')]: {
                    ...visuallyhiddenOff,
                },
            },
            ({ topicHeaderType, isGenericTopicPage }) =>
                topicHeaderType
                    ? {
                          margin: calcRem(0, metricsPN.spacing.S),
                          marginTop: calcRem(metricsPN.spacing['L']),
                          marginBottom: isGenericTopicPage
                              ? 0
                              : calcRem(metricsPN.spacing['XL']),
                          gap:
                              topicHeaderType === 'secondary' ||
                              topicHeaderType === 'tertiary'
                                  ? 0
                                  : calcRem(metricsPN.spacing['4XS']),

                          [breakpoint('sm')]: {
                              marginTop: calcRem(metricsPN.spacing['L']),
                              marginBottom: isGenericTopicPage
                                  ? 0
                                  : calcRem(metricsPN.layout.section['tablet']),
                          },
                          [breakpoint('lg')]: {
                              margin: calcRem(0),
                              marginTop: calcRem(
                                  metricsPN.layout.section['desktop'],
                              ),
                              marginBottom: isGenericTopicPage
                                  ? 0
                                  : calcRem(
                                        metricsPN.layout.section['desktop'],
                                    ),
                          },
                      }
                    : {},
            themeOverride &&
                sectionHeaderContainerThemeOverrides[themeOverride],
        ]
    },
)
export interface StyledHeadingProps {
    mainSection?: boolean
    hasPadding?: boolean
    hasLogo?: boolean
    isTopicPageHeader?: boolean
}

export const Heading = styled.h2<StyledHeadingProps>(
    ({ mainSection, hasLogo, isTopicPageHeader }) => {
        const headerMixin = mapTitleMixin(mainSection ? '2XL' : 'M')
        return [
            {
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'row',
                margin: 0,
                position: 'relative',
                color: isTopicPageHeader
                    ? colorsPN.text.primary
                    : colorsPN.text.brand,
                flex: 1,
                wordBreak: 'keep-all',
                svg: hasLogo
                    ? {
                          height: calcRem(22),
                          marginLeft: calcRem(3),
                          transform: 'translateY(1px)',
                      }
                    : undefined,
                ...headerMixin.small.bold,
                [breakpoint('sm')]: headerMixin.large.bold,
            },
        ]
    },
)

export const HeadingWrapper = styled.div<{ themeOverride?: ThemeOverride }>(
    ({ themeOverride }) => {
        return [
            {
                display: 'flex',
                flexGrow: 1,
                flexDirection: 'row',
                gap: calcRem(metricsPN.spacing['4XS']),
                alignItems: 'center',
                minHeight: calcRem(44),
                width: '100%',
                justifyContent: 'space-between',
            },
            themeOverride && sectionHeaderThemeOverrides[themeOverride],
        ]
    },
)

export const HeadingWithLogoWrapper = styled.div(() => {
    return {
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        flexWrap: 'wrap',
        flexGrow: 1,
        gap: calcRem(10),
    }
})

export const HeadingLink = styled(WebLink)<{ hideOnMobile?: boolean }>(
    ({ hideOnMobile = 'false' }) => {
        return {
            textDecoration: 'none',
            zIndex: zIndex.common.link,
            cursor: 'pointer',
            transition: `background-color ${easing.easeOut.fast}`,
            color: colorsPN.text.brand,
            display: hideOnMobile ? 'none' : 'flex',
            alignItems: 'center',
            ...perthNowNGNTextMixin.label.L.medium,

            '&:focus-visible': {
                outline: 'none',
                textDecoration: 'underline',
            },

            '&:hover': {
                textDecoration: 'underline',
            },

            [breakpoint('md')]: {
                display: 'flex',
            },
        }
    },
)

export const StyledNav = styled.nav(() => {
    const sideMargins = metricsPN.spacing['S'] * 2

    return {
        // Extend the width of the nav to account for the side paddings
        width: `calc(100% + ${calcRem(sideMargins)})`,
        overflowX: 'scroll',
        margin: calcRem(0, -metricsPN.spacing['S']),
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': {
            display: 'none',
        },
        [breakpoint('md')]: {
            width: '100%',
            margin: 0,
            overflowX: 'unset',
        },
    }
})

export const NavLinkList = styled.ul({
    listStyle: 'none',
    margin: 0,
    width: '100%',
    flexDirection: 'row',
    flex: 1,
    justifyContent: 'flex-start',
    alignItems: 'center',
    display: 'flex',
    gap: `0 ${calcRem(metricsPN.spacing['XL'])}`,
    flexWrap: 'unset',
    paddingLeft: calcRem(metricsPN.spacing['S']),
    paddingTop: calcRem(metricsPN.spacing['3XS']),
    paddingBottom: calcRem(metricsPN.spacing['3XS']),

    [breakpoint('md')]: {
        paddingLeft: 0,
        flexWrap: 'wrap',
        overflowX: 'unset',
    },
})

export const NavLinkWrapper = styled.li({
    width: 'auto',
    flex: '0 1 auto',
    textAlign: 'center',
})

export const LocalNavLink = styled(Link)(({}) => {
    return [
        {
            display: 'block',
            whiteSpace: 'nowrap',
            position: 'relative',
            textDecoration: 'none',
            height: '100%',
            paddingTop: calcRem(metricsPN.spacing['3XS']),
            paddingBottom: calcRem(metricsPN.spacing['3XS']),
            zIndex: zIndex.common.link,

            '&:focus-visible': {
                textDecoration: 'underline',
                outline: 'revert',
                outlineOffset: calcRem(metricsPN.spacing['4XS']),
                borderRadius: `${metricsPN.radius['S']}px`,
            },

            color: colorsPN.text.primary,

            '&:hover': {
                textDecoration: 'underline',
            },

            '&:active': {
                color: colorsPN.text.brand,
            },
        },
        perthNowNGNTextMixin.label.L.medium,
    ]
})

export const StyledSponsoredLogo = styled('img')(({ theme }) => ({
    height: calcRem(30),
    maxWidth: '100%',
    paddingLeft: calcRem(2),
    [breakpoint('xxs')]: {
        display: 'block',
        height: calcRem(40),
        paddingLeft: calcRem(24),
    },
}))

export const WebNavLink = LocalNavLink.withComponent(WebLink)
